import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Eye, ExternalLink, Mail, Loader2, ListChecks, FileDown } from 'lucide-react';
import type { ProfilePdfLang } from '@/lib/generateCompanyProfilePdf';
import { toast } from 'sonner';
import { COMPLIANCE_GROUPS, isTranslationKey } from '@/data/compliance';
import { PHONE_NUMBER_DISPLAY } from '@/constants/contact';
import ProfileManifestViewer from '@/components/profile/ProfileManifestViewer';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  CURRENT_VERSION,
  getProfileUrl,
  getProfileDownloadName,
  type ProfileLang,
} from '@/constants/companyProfile';
import { useProfileAvailability } from '@/hooks/useProfileAvailability';

interface CompanyProfileCTAProps {
  variant?: 'section' | 'inline';
}

const CompanyProfileCTA = ({ variant = 'section' }: CompanyProfileCTAProps) => {
  const { t, language } = useLanguage();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState<ProfileLang>(language);
  const [showManifest, setShowManifest] = useState(false);
  const availability = useProfileAvailability();
  const [generating, setGenerating] = useState(false);

  const handleGeneratePdf = useCallback(async (lang: ProfilePdfLang) => {
    setGenerating(true);
    try {
      const serviceKeys = ['buildings', 'civil', 'vrd', 'renovation', 'earthworks', 'hydraulics'];
      const commitmentKeys = ['legislation', 'procurement', 'safety', 'corruption', 'ethics', 'qse'];
      const now = new Date();
      const dateStr = now.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

      const certRows = COMPLIANCE_GROUPS.flatMap((g) =>
        g.items.map((item) => ({
          name: t(item.labelKey),
          category: t(g.titleKey),
          reference: isTranslationKey(item.value) ? t(item.value) : item.value,
          status: t(`certs.status.${item.status ?? 'valid'}`),
          statusKind: (item.status ?? 'valid') as 'valid' | 'inProgress' | 'expired',
        })),
      );

      const { generateCompanyProfilePdf } = await import('@/lib/generateCompanyProfilePdf');
      generateCompanyProfilePdf({
        language: lang,
        labels: {
          companyName: 'MTB BTP',
          tagline: t('hero.subtitle'),
          subtitle: t('profilePdf.subtitle'),
          generatedOn: dateStr,
          version: `v${CURRENT_VERSION.version}`,
          aboutTitle: t('about.title'),
          aboutIntro: t('about.intro'),
          aboutGroup: t('about.group'),
          aboutValues: t('about.values'),
          visionTitle: t('about.vision.title'),
          visionDesc: t('about.vision.desc'),
          missionTitle: t('about.mission.title'),
          missionDesc: t('about.mission.desc'),
          servicesTitle: t('services.title'),
          servicesIntro: t('services.intro'),
          servicesNote: t('services.note'),
          complianceTitle: t('compliance.title'),
          complianceIntro: t('compliance.intro'),
          complianceCommitment: t('compliance.commitment'),
          certsTitle: t('certs.title'),
          certsDisclaimer: t('certs.disclaimer'),
          colName: t('manifestPage.exportPdf.col.name'),
          colCategory: t('manifestPage.exportPdf.col.type'),
          colReference: t('manifestPage.exportPdf.col.reference'),
          colStatus: t('manifestPage.exportPdf.col.status'),
          contactTitle: t('profilePdf.contactTitle'),
          address: t('contact.address.value'),
          phone: PHONE_NUMBER_DISPLAY,
          website: 'www.mtbbtp.com',
          filename: lang === 'fr' ? 'MTB_BTP_Profil_Entreprise' : 'MTB_BTP_Company_Profile',
          page: t('manifestPage.exportPdf.page'),
          confidential: t('profilePdf.confidential'),
        },
        services: serviceKeys.map((k) => ({
          title: t(`services.${k}.title`),
          description: t(`services.${k}.desc`),
        })),
        commitments: commitmentKeys.map((k) => ({
          title: t(`compliance.${k}.title`),
          description: t(`compliance.${k}.desc`),
        })),
        certifications: certRows,
      });

      toast.success(lang === 'fr' ? 'PDF généré avec succès' : 'PDF generated successfully');
    } catch (error) {
      console.error('[CompanyProfilePdf] Generation failed:', error);
      toast.error(
        lang === 'fr'
          ? 'La génération du PDF a échoué. Veuillez réessayer.'
          : 'PDF generation failed. Please try again.',
      );
    } finally {
      setGenerating(false);
    }
  }, [t, language]);

  const openPreview = (lang: ProfileLang) => {
    setPreviewLang(lang);
    setPreviewOpen(true);
  };

  const currentPdf = getProfileUrl(previewLang);
  const currentFileName = getProfileDownloadName(previewLang);
  const versionLabel = `v${CURRENT_VERSION.version} · ${CURRENT_VERSION.releaseDate}`;

  const isChecking =
    availability.fr === 'checking' || availability.en === 'checking';
  const anyAvailable =
    availability.fr === 'available' || availability.en === 'available';
  const allMissing =
    availability.fr === 'missing' && availability.en === 'missing';

  /**
   * Small inline status badge — gives the user (and ops) instant visibility
   * on whether a localized PDF is reachable, missing, or still being checked.
   */
  const renderStatusBadge = (status: 'available' | 'missing' | 'checking') => {
    const map = {
      available: {
        dot: 'bg-emerald-500',
        text: 'text-emerald-700 dark:text-emerald-400',
        ring: 'border-emerald-500/30 bg-emerald-500/10',
        label: t('cta.profile.status.available'),
        aria: t('cta.profile.status.available.aria'),
      },
      missing: {
        dot: 'bg-destructive',
        text: 'text-destructive',
        ring: 'border-destructive/30 bg-destructive/10',
        label: t('cta.profile.status.missing'),
        aria: t('cta.profile.status.missing.aria'),
      },
      checking: {
        dot: 'bg-amber-500 animate-pulse',
        text: 'text-amber-700 dark:text-amber-400',
        ring: 'border-amber-500/30 bg-amber-500/10',
        label: t('cta.profile.status.checking'),
        aria: t('cta.profile.status.checking.aria'),
      },
    } as const;
    const s = map[status];
    return (
      <span
        role="status"
        aria-label={s.aria}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${s.ring} ${s.text}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
        {s.label}
      </span>
    );
  };

  // Renders a single language download item, or a disabled "On request" row.
  const renderLangItem = (lang: ProfileLang) => {
    const status = availability[lang];
    const labelKey = lang === 'fr' ? 'cta.profile.fr' : 'cta.profile.en';
    const ariaKey =
      lang === 'fr' ? 'cta.profile.aria.download.fr' : 'cta.profile.aria.download.en';

    if (status === 'available') {
      return (
        <DropdownMenuItem asChild key={lang}>
          <a
            href={getProfileUrl(lang)}
            download={getProfileDownloadName(lang)}
            hrefLang={lang}
            aria-label={t(ariaKey)}
            className="flex items-center gap-2 cursor-pointer focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-popover rounded-sm"
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            <span className="flex-1">{t(labelKey)}</span>
            {renderStatusBadge('available')}
          </a>
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem
        key={lang}
        disabled
        aria-label={t('cta.profile.onRequest.aria')}
        className="flex items-center gap-2 opacity-70"
      >
        {status === 'checking' ? (
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        ) : (
          <FileText className="w-4 h-4" aria-hidden="true" />
        )}
        <span className="flex-1">{t(labelKey)}</span>
        {renderStatusBadge(status === 'checking' ? 'checking' : 'missing')}
      </DropdownMenuItem>
    );
  };

  const trigger = (
    <div className="inline-flex flex-wrap items-center justify-center gap-3">
      {/* Preview button — opens modal dialog */}
      <Button
        size="lg"
        variant="outline"
        onClick={() => openPreview(language)}
        aria-label={t('cta.profile.aria.preview')}
        aria-haspopup="dialog"
        aria-expanded={previewOpen}
        aria-controls="company-profile-preview-dialog"
        className="border-accent/40 text-foreground hover:bg-accent/10 hover:border-accent font-semibold focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <Eye className="mr-2 w-5 h-5" aria-hidden="true" />
        {t('cta.profile.preview')}
      </Button>

      {/* Download dropdown — appears only if at least one PDF is reachable.
          When both files are missing, we surface a clear "On request" CTA
          that routes the visitor to /contact instead. */}
      {allMissing && !isChecking ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                size="lg"
                variant="outline"
                aria-label={t('cta.profile.onRequest.aria')}
                className="border-accent/40 text-foreground hover:bg-accent/10 hover:border-accent font-semibold"
              >
                <Link to={`/contact?topic=company-profile&lang=${language}`}>
                  <Mail className="mr-2 w-5 h-5" aria-hidden="true" />
                  {t('cta.profile.onRequest')}
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('cta.profile.onRequest.tooltip')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="lg"
              disabled={isChecking || !anyAvailable}
              aria-label={`${t('cta.profile.button')} – ${t('cta.profile.aria.download.menu')}`}
              className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark font-semibold shadow-lg shadow-accent/25 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {isChecking ? (
                <Loader2 className="mr-2 w-5 h-5 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="mr-2 w-5 h-5" aria-hidden="true" />
              )}
              {isChecking ? t('cta.profile.checking') : t('cta.profile.button')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            className="bg-popover"
            aria-label={t('cta.profile.aria.download.menu')}
          >
            {renderLangItem('fr')}
            {renderLangItem('en')}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Generate PDF on-the-fly — uses current language */}
      <Button
        size="lg"
        variant="outline"
        disabled={generating}
        onClick={() => handleGeneratePdf(language)}
        aria-label={`${t('profilePdf.generate')} – ${t('profilePdf.generate.aria')}`}
        className="border-accent/40 text-foreground hover:bg-accent/10 hover:border-accent font-semibold"
      >
        {generating ? (
          <Loader2 className="mr-2 w-5 h-5 animate-spin" aria-hidden="true" />
        ) : (
          <FileDown className="mr-2 w-5 h-5" aria-hidden="true" />
        )}
        {t('profilePdf.generate')}
      </Button>
    </div>
  );

  const previewModal = (
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogContent
        id="company-profile-preview-dialog"
        aria-describedby="company-profile-preview-desc"
        className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0 bg-card"
      >
        <DialogHeader className="p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col">
              <DialogTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-accent" aria-hidden="true" />
                {t('cta.profile.preview.title')}
              </DialogTitle>
              <span className="text-xs text-muted-foreground mt-0.5 ml-7">
                {versionLabel}
              </span>
              {/* Hidden description for screen readers (Radix a11y requirement) */}
              <DialogDescription
                id="company-profile-preview-desc"
                className="sr-only"
              >
                {t('cta.profile.preview.desc')}
              </DialogDescription>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Language switcher inside the modal */}
              <div
                role="group"
                aria-label={t('cta.profile.preview.lang')}
                className="inline-flex rounded-md border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setPreviewLang('fr')}
                  aria-pressed={previewLang === 'fr'}
                  aria-label={t('cta.profile.aria.lang.fr')}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset ${
                    previewLang === 'fr'
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background text-muted-foreground hover:bg-secondary'
                  }`}
                >
                  FR
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewLang('en')}
                  aria-pressed={previewLang === 'en'}
                  aria-label={t('cta.profile.aria.lang.en')}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset ${
                    previewLang === 'en'
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-background text-muted-foreground hover:bg-secondary'
                  }`}
                >
                  EN
                </button>
              </div>

              {/* Manifest toggle */}
              <Button
                size="sm"
                variant={showManifest ? 'default' : 'outline'}
                onClick={() => setShowManifest((v) => !v)}
                aria-pressed={showManifest}
                aria-controls="company-profile-manifest-panel"
                className={showManifest ? 'bg-accent text-accent-foreground hover:bg-mtb-gold-dark' : ''}
              >
                <ListChecks className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline ml-1.5">
                  {showManifest ? t('manifest.toggle.hide') : t('manifest.toggle.show')}
                </span>
              </Button>

              {availability[previewLang] === 'available' ? (
                <>
                  <Button asChild size="sm" variant="outline">
                    <a
                      href={currentPdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      hrefLang={previewLang}
                      aria-label={t('cta.profile.preview.open')}
                      className="flex items-center gap-1.5"
                    >
                      <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">{t('cta.profile.preview.open')}</span>
                    </a>
                  </Button>

                  <Button
                    asChild
                    size="sm"
                    className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark"
                  >
                    <a
                      href={currentPdf}
                      download={currentFileName}
                      hrefLang={previewLang}
                      aria-label={
                        previewLang === 'fr'
                          ? t('cta.profile.aria.download.fr')
                          : t('cta.profile.aria.download.en')
                      }
                      className="flex items-center gap-1.5"
                    >
                      <Download className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">{t('cta.profile.preview.download')}</span>
                    </a>
                  </Button>
                </>
              ) : (
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  aria-label={t('cta.profile.onRequest.aria')}
                >
                  <Link to={`/contact?topic=company-profile&lang=${previewLang}`} className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4" aria-hidden="true" />
                    <span className="hidden sm:inline">{t('cta.profile.onRequest')}</span>
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 bg-muted overflow-hidden flex flex-col md:flex-row min-h-0">
          <div className="flex-1 min-h-0 min-w-0">
          <object
            key={currentPdf}
            data={`${currentPdf}#view=FitH&toolbar=1`}
            type="application/pdf"
            title={t('cta.profile.viewer.title')}
            aria-label={t('cta.profile.viewer.title')}
            className="w-full h-full"
          >
            <div
              role="region"
              aria-label={t('cta.profile.preview.fallback')}
              className="flex flex-col items-center justify-center h-full p-6 text-center gap-4"
            >
              <FileText className="w-12 h-12 text-muted-foreground" aria-hidden="true" />
              <p className="text-muted-foreground max-w-md">
                {t('cta.profile.preview.fallback')}
              </p>
              {availability[previewLang] === 'available' ? (
                <Button asChild className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark">
                  <a
                    href={currentPdf}
                    download={currentFileName}
                    hrefLang={previewLang}
                    aria-label={
                      previewLang === 'fr'
                        ? t('cta.profile.aria.download.fr')
                        : t('cta.profile.aria.download.en')
                    }
                  >
                    <Download className="mr-2 w-4 h-4" aria-hidden="true" />
                    {t('cta.profile.preview.download')}
                  </a>
                </Button>
              ) : (
                <Button
                  asChild
                  variant="outline"
                  aria-label={t('cta.profile.onRequest.aria')}
                >
                  <Link to={`/contact?topic=company-profile&lang=${previewLang}`}>
                    <Mail className="mr-2 w-4 h-4" aria-hidden="true" />
                    {t('cta.profile.onRequest')}
                  </Link>
                </Button>
              )}
            </div>
          </object>
          </div>
          {showManifest && (
            <aside
              id="company-profile-manifest-panel"
              aria-label={t('manifest.compliance.title')}
              className="w-full md:w-96 md:max-w-[40%] md:border-l border-t md:border-t-0 border-border bg-background overflow-y-auto"
            >
              <ProfileManifestViewer />
            </aside>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  if (variant === 'inline') {
    return (
      <>
        <div className="inline-flex">{trigger}</div>
        {previewModal}
      </>
    );
  }

  return (
    <>
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-card border border-border rounded-2xl p-8 md:p-10 shadow-lg text-center">
            <div className="inline-flex p-4 bg-accent/10 rounded-2xl mb-5">
              <FileText className="w-8 h-8 text-accent" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3">
              {t('cta.profile.title')}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              {t('cta.profile.desc')}
            </p>
            {trigger}
          </div>
        </div>
      </section>
      {previewModal}
    </>
  );
};

export default CompanyProfileCTA;
